import { useEffect, useState } from "react";
import axios from "axios";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowPathIcon, BuildingOffice2Icon, UserPlusIcon, UsersIcon } from "@heroicons/react/24/outline";
import { toast } from "../utils/toast";
import { hasAnyRole } from "../utils/auth";
import { ROLES } from "../auth/roles";
import { Button, Container, MetricCard, PageHeader, RowAction, Table, Tag } from "./ui";
import AssignInstitutionModal from "./users/AssignInstitutionModal";
import CreateAccountModal from "./users/CreateAccountModal";

const PAGE_SIZE = 10;
const USERS_KEY = "adminUsers";
// Account creation is hidden for now; set to true to bring the button back
const SHOW_CREATE_ACCOUNT = false;

// Admins look users up across the whole system, create staff accounts and link HLI admins to their institution
export default function AdminUserManagement() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [assigning, setAssigning] = useState(null);
  const [creating, setCreating] = useState(false);
  const isAdmin = hasAnyRole(ROLES.ADMIN);

  // The server searches every page, so wait for typing to pause and start again from the first page
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // The bearer token is added by the axios interceptor (src/auth/http.js)
  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: [USERS_KEY, page, debouncedSearch],
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    queryFn: async () => {
      try {
        const params = { page, size: PAGE_SIZE, ...(debouncedSearch && { search: debouncedSearch }) };
        return (await axios.get(`${process.env.REACT_APP_BASE_URL}hec/users/admin/all`, { params })).data;
      } catch (error) {
        console.error("Failed to load users:", error);
        toast.error("Failed to load users", { id: "admin-users-error" });
        throw error;
      }
    },
  });

  const totalElements = data?.totalElements ?? 0;
  const rows = (data?.content ?? []).map((u, i) => ({ ...u, index: page * PAGE_SIZE + i + 1 }));

  const columns = [
    { key: "index", label: "#", className: "text-slate-500" },
    { key: "name", label: "Name", accessor: (u) => [u.firstName, u.lastName].filter(Boolean).join(" ") || "—", className: "font-semibold text-slate-900" },
    { key: "email", label: "Email" },
    { key: "nationalId", label: "National ID" },
    { key: "phoneNumber", label: "Phone" },
    {
      key: "roleName",
      label: "Role",
      render: (u) => (u.roleName ? <Tag color="brand">{u.roleName}</Tag> : <span className="text-xs italic text-slate-400">No role</span>),
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            label: "",
            exportable: false,
            render: (u) =>
              u.roleName === "HLI_ADMIN" && (
                <div className="flex justify-end">
                  <RowAction label="Assign institution" Icon={BuildingOffice2Icon} onClick={() => setAssigning(u)} />
                </div>
              ),
          },
        ]
      : []),
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="User Management"
        subtitle={SHOW_CREATE_ACCOUNT ? "Search for users, create staff accounts and manage their institution" : "Search for users and manage their institution"}
        actions={
          <>
            <Button variant="ghost" icon={<ArrowPathIcon className="h-4 w-4" />} onClick={() => refetch()} loading={isFetching && !isPending}>
              Refresh
            </Button>
            {SHOW_CREATE_ACCOUNT && isAdmin && (
              <Button icon={<UserPlusIcon className="h-4 w-4" />} onClick={() => setCreating(true)}>
                Create account
              </Button>
            )}
          </>
        }
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard title={debouncedSearch ? "Matching users" : "Total users"} value={totalElements.toLocaleString("en-US")} icon={<UsersIcon className="h-4 w-4 text-slate-400" />} />
      </div>
      <Table cardView
        columns={columns}
        data={rows}
        isLoading={isPending}
        searchPlaceholder="Search by name, email or national ID"
        searchValue={search}
        onSearchChange={setSearch}
        exportName="users"
        emptyTitle={debouncedSearch ? "No users match your search" : "No users found"}
        pagination={{ page, totalPages: data?.totalPages ?? 0, totalElements, pageSize: PAGE_SIZE, onPageChange: setPage }}
      />
      <CreateAccountModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        }}
      />
      <AssignInstitutionModal
        user={assigning}
        onClose={() => setAssigning(null)}
        onSuccess={() => {
          setAssigning(null);
          queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        }}
      />
    </Container>
  );
}
